/**
 * Profile Settings Component
 *
 * Gestion du profil utilisateur personnel
 *
 * Fonctionnalités:
 * - Édition des informations personnelles
 * - Gestion de l'avatar
 * - Personnalisation du background
 * - Préférences utilisateur (langue, timezone)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  User,
  Camera,
  Mail,
  Phone,
  Globe,
  Clock,
  ChevronRight,
  Loader2,
  Check,
  X,
  Palette,
  Edit3,
  Save,
} from 'lucide-react';
import { BackgroundPicker } from './BackgroundPicker';
import { profileApi, UserProfile, BackgroundType } from '../api/profile.api';
import { useAuthStore } from '../hooks/useAuth';
import { logger } from '@/utils/logger';

type ActiveSection = 'overview' | 'edit-profile' | 'background' | 'preferences';

export interface ProfileSettingsProps {
  /** Classe CSS additionnelle */
  className?: string;
  /** Section initialement active */
  initialSection?: ActiveSection;
}

export const ProfileSettings: React.FC<ProfileSettingsProps> = ({
  className = '',
  initialSection = 'overview',
}) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<ActiveSection>(initialSection);
  const { user, refreshUser } = useAuthStore();

  // Profile state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Edit form state
  const [editForm, setEditForm] = useState({
    fullName: '',
    phone: '',
    username: '',
    locale: '',
    timezone: '',
  });

  // Avatar upload
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await profileApi.getMe();

        // Vérifier que les données sont valides
        if (!data || !data.id) {
          throw new Error(t('auth.profile.profileDataError'));
        }

        setProfile(data);
        setEditForm({
          fullName: data.fullName || '',
          phone: data.phone || '',
          username: data.username || '',
          locale: data.locale || 'fr-FR',
          timezone: data.timezone || 'Europe/Paris',
        });
        logger.debug('Profile loaded:', data);
      } catch (err) {
        logger.error('Failed to fetch profile:', err);
        setError(t('auth.profile.loadError'));

        // Fallback: utiliser les données du store auth
        if (user) {
          setProfile({
            id: user.id,
            email: user.email,
            fullName: user.fullName || null,
            phone: user.phone || null,
            avatar: null,
            username: user.username || null,
            locale: user.locale || null,
            timezone: user.timezone || null,
            backgroundType: user.backgroundType || 'NONE',
            background: user.background || null,
          });
          setEditForm({
            fullName: user.fullName || '',
            phone: user.phone || '',
            username: user.username || '',
            locale: user.locale || 'fr-FR',
            timezone: user.timezone || 'Europe/Paris',
          });
          setError(null); // Clear error since we have fallback data
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  // Refresh profile when returning to overview
  const handleSectionChange = (section: ActiveSection) => {
    setActiveSection(section);
    setError(null);
    setSuccess(null);
  };

  // Handle profile update
  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);

      const updated = await profileApi.updateProfile({
        fullName: editForm.fullName || null,
        phone: editForm.phone || null,
        username: editForm.username || null,
        locale: editForm.locale || null,
        timezone: editForm.timezone || null,
      });

      setProfile(updated);
      setSuccess(t('auth.profile.saveSuccess'));

      // Refresh auth store user
      if (refreshUser) {
        await refreshUser();
      }

      setTimeout(() => {
        handleSectionChange('overview');
      }, 1500);
    } catch (err) {
      logger.error('Failed to update profile:', err);
      setError(t('auth.profile.updateError'));
    } finally {
      setSaving(false);
    }
  };

  // Handle background change
  const handleBackgroundChange = (backgroundType: BackgroundType, background: string | null) => {
    setProfile((prev) => prev ? { ...prev, backgroundType, background } : null);
    setSuccess(t('auth.profile.backgroundUpdated'));

    // Refresh auth store user
    if (refreshUser) {
      refreshUser();
    }

    setTimeout(() => setSuccess(null), 3000);
  };

  // Handle avatar upload (placeholder - would need backend endpoint)
  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validation
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('auth.profile.avatarFormatError'));
      return;
    }

    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setError(t('auth.profile.avatarSizeError'));
      return;
    }

    try {
      setUploadingAvatar(true);
      setError(null);

      const result = await profileApi.uploadAvatar(file);
      setProfile((prev) => prev ? { ...prev, avatar: result.avatar } : null);

      setSuccess(t('auth.profile.avatarUpdated'));

      // Refresh auth store user
      if (refreshUser) {
        await refreshUser();
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      logger.error('Failed to upload avatar:', err);
      setError(t('auth.profile.avatarUploadError'));
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 overflow-hidden hover:bg-white/70 hover:shadow-2xl
    transition-all duration-300`;

  const menuItemClasses = `flex items-center justify-between p-4 cursor-pointer transition-all duration-200
    hover:bg-white/50 border-l-4 border-transparent rounded-xl bg-white/30`;

  const inputClasses = `w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900
    placeholder-gray-400 focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all`;

  // Get initials for avatar
  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        </div>
      );
    }

    switch (activeSection) {
      case 'edit-profile':
        return (
          <div className="p-6 space-y-6">
            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.profile.fullName')}
                </label>
                <input
                  type="text"
                  value={editForm.fullName}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, fullName: e.target.value }))}
                  placeholder={t('auth.profile.placeholders.fullName')}
                  className={inputClasses}
                />
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.profile.username')}
                </label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, username: e.target.value }))}
                  placeholder={t('auth.profile.placeholders.username')}
                  className={inputClasses}
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.profile.phone')}
                </label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder={t('auth.profile.placeholders.phone')}
                  className={inputClasses}
                />
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.profile.email')}
                </label>
                <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-200">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">{profile?.email}</span>
                  <span className="ml-auto text-xs text-gray-400">{t('auth.profile.notEditable')}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => handleSectionChange('overview')}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3
                  text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl
                  transition-colors font-medium"
              >
                <X className="w-4 h-4" />
                {t('auth.profile.cancel')}
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3
                  text-white bg-teal-600 hover:bg-teal-700 rounded-xl
                  transition-colors font-medium disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? t('auth.profile.saving') : t('auth.profile.save')}
              </button>
            </div>
          </div>
        );

      case 'background':
        return (
          <div className="p-6">
            <BackgroundPicker
              currentBackground={profile?.background || null}
              currentBackgroundType={profile?.backgroundType || 'NONE'}
              onBackgroundChange={handleBackgroundChange}
            />
          </div>
        );

      case 'preferences':
        return (
          <div className="p-6 space-y-6">
            <div className="space-y-4">
              {/* Locale */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {t('auth.profile.language')}
                  </div>
                </label>
                <select
                  value={editForm.locale}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, locale: e.target.value }))}
                  className={inputClasses}
                >
                  <option value="fr-FR">{t('auth.profile.localeOptions.frFR')}</option>
                  <option value="en-US">{t('auth.profile.localeOptions.enUS')}</option>
                  <option value="en-GB">{t('auth.profile.localeOptions.enGB')}</option>
                  <option value="de-DE">{t('auth.profile.localeOptions.deDE')}</option>
                  <option value="es-ES">{t('auth.profile.localeOptions.esES')}</option>
                  <option value="it-IT">{t('auth.profile.localeOptions.itIT')}</option>
                </select>
              </div>

              {/* Timezone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {t('auth.profile.timezone')}
                  </div>
                </label>
                <select
                  value={editForm.timezone}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, timezone: e.target.value }))}
                  className={inputClasses}
                >
                  <option value="Europe/Paris">{t('auth.profile.timezoneOptions.europeParis')}</option>
                  <option value="Europe/London">{t('auth.profile.timezoneOptions.europeLondon')}</option>
                  <option value="America/New_York">{t('auth.profile.timezoneOptions.americaNewYork')}</option>
                  <option value="America/Los_Angeles">{t('auth.profile.timezoneOptions.americaLosAngeles')}</option>
                  <option value="Asia/Tokyo">{t('auth.profile.timezoneOptions.asiaTokyo')}</option>
                  <option value="Australia/Sydney">{t('auth.profile.timezoneOptions.australiaSydney')}</option>
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => handleSectionChange('overview')}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3
                  text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl
                  transition-colors font-medium"
              >
                <X className="w-4 h-4" />
                {t('auth.profile.cancel')}
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3
                  text-white bg-teal-600 hover:bg-teal-700 rounded-xl
                  transition-colors font-medium disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? t('auth.profile.saving') : t('auth.profile.save')}
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="p-6 space-y-6">
            {/* Profile Header */}
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="relative group">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-400 to-teal-600
                  flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                  {profile?.avatar ? (
                    <img
                      src={profile.avatar}
                      alt={profile.fullName || 'Avatar'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    getInitials(profile?.fullName ?? null)
                  )}
                </div>
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="absolute inset-0 flex items-center justify-center
                    bg-black/50 opacity-0 group-hover:opacity-100 rounded-2xl
                    transition-opacity cursor-pointer"
                >
                  {uploadingAvatar ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <Camera className="w-6 h-6 text-white" />
                  )}
                </button>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
              </div>

              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900">
                  {profile?.fullName || t('auth.profile.defaultUser')}
                </h3>
                <p className="text-sm text-gray-500">{profile?.email}</p>
                {profile?.username && (
                  <p className="text-xs text-teal-600">@{profile.username}</p>
                )}
              </div>
            </div>

            {/* Quick Info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-3 bg-white/40 rounded-xl">
                <Phone className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {profile?.phone || t('auth.profile.notProvided')}
                </span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-white/40 rounded-xl">
                <Globe className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {profile?.locale || 'fr-FR'}
                </span>
              </div>
            </div>

            {/* Menu Items */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 mb-3">{t('auth.profile.customization')}</h4>

              {/* Edit Profile */}
              <button
                onClick={() => handleSectionChange('edit-profile')}
                className={menuItemClasses + ' w-full'}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <Edit3 className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{t('auth.profile.editProfile')}</p>
                    <p className="text-xs text-gray-500">{t('auth.profile.editProfileSubtitle')}</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </button>

              {/* Background */}
              <button
                onClick={() => handleSectionChange('background')}
                className={menuItemClasses + ' w-full'}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Palette className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{t('auth.profile.background')}</p>
                    <p className="text-xs text-gray-500">
                      {profile?.backgroundType === 'NONE'
                        ? t('auth.profile.backgroundDefault')
                        : profile?.backgroundType === 'CUSTOM'
                          ? t('auth.profile.backgroundCustom')
                          : t('auth.profile.backgroundPredefined')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {profile?.backgroundType !== 'NONE' && (
                    <Check className="w-4 h-4 text-green-500" />
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </button>

              {/* Preferences */}
              <button
                onClick={() => handleSectionChange('preferences')}
                className={menuItemClasses + ' w-full'}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Globe className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{t('auth.profile.preferences')}</p>
                    <p className="text-xs text-gray-500">{t('auth.profile.preferencesSubtitle')}</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-teal-100 rounded-2xl">
          <User className="w-8 h-8 text-teal-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('auth.profile.title')}</h2>
          <p className="text-gray-500">{t('auth.profile.subtitle')}</p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600">
          <X className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl text-green-600">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Main Content */}
      <div className={cardClasses}>
        {/* Section Header with Back Button */}
        {activeSection !== 'overview' && (
          <div className="bg-gray-50/50 border-b border-gray-100 px-6 py-4">
            <button
              onClick={() => handleSectionChange('overview')}
              className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 transition-colors"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              {t('auth.profile.backToPreview')}
            </button>
          </div>
        )}

        {renderContent()}
      </div>
    </div>
  );
};

export default ProfileSettings;
